import React, { memo, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Container,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import css from 'styled-jsx/css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faEyeSlash,
  faCircleNotch,
} from '@fortawesome/free-solid-svg-icons';

import BrandingContext from '../../Context/BrandingContext';

import CenterContainer from '../CenterContainer';
import { ERROR_LOGIN_FORM } from '../../Constants/TrackerClasses';
import TermsAndConditions from './TermsAndConditionsTemplate';
import ResetPasswordModal from './ResetPasswordContainer';

export default memo(LoginDisplay);

const { className: alertClass, styles: alertStyles } = css.resolve`
  .alert {
    z-index: 10000;
    right: 12.5%;
    top: 5rem;
    opacity: 0.9;
  }
`;

const { className: inputClass, styles: inputStyles } = css.resolve`
  input {
    border-radius: 15px;
  }
  input::placeholder {
    color: var(--icon-link-gray);
  }
  .form-control:focus {
    outline: none;
  }
  input::-webkit-credentials-auto-fill-button {
    visibility: hidden;
    display: none !important;
    pointer-events: none;
    position: absolute;
    right: 0;
  }
  input:-webkit-autofill {
    -webkit-box-shadow: 0 0 0px 1000px white inset;
  }
`;

const { className: eyeButtonClass, styles: eyeButtonStyles } = css.resolve`
  button {
    color: #21363e;
    outline: none;
    border-color: var(--input-border-gray);
    border-radius: 0 15px 15px 0;
  }

  button:active {
    border-color: var(--input-border-gray);
    border-style: double;
    outline: transparent;
  }
`;

const { className: modalClass, styles: modalStyles } = css.resolve`
  @media (max-width: 767px) {
    .modal-dialog {
      height: 100%;
      width: 100%;
      max-height: 100%;
      max-width: 100%;
      margin: 0;
    }
    .modal-body {
      max-height: calc(100vh - 123px);
      overflow-y: auto;
    }
  }
  @media (min-width: 768px) {
    .modal-body {
      max-height: 60vh;
      overflow-y: auto;
    }
  }
`;

function LoginDisplay(props) {
  const {
    alertColor,
    alertMessage,
    alertText,
    email,
    error,
    handleSubmit,
    handlePropUpdate,
    hidePassword,
    loginRequestPending,
    password,
    showAlert,
    showError,
    showResetPasswordAlert,
    termsModal,
    toggleAlert,
    togglePasswordType,
    toggleTermsAndConditionsModal,
  } = props;

  const [showResetPasswordModal, toggleResetPasswordModal] = useState(false);

  const {
    isElevatUi,
    isEnovationUi,
    isSupraUi,
    isVersatileUi,
    logo_url,
    tige_login_logo_url,
  } = useContext(BrandingContext);

  const isTigeDemoUi = window.location.host?.includes('tige');
  const hideConnectingText = isVersatileUi || isSupraUi || isTigeDemoUi;

  return (
    <CenterContainer>
      <Alert
        color={alertColor}
        isOpen={showAlert}
        toggle={toggleAlert}
        className={`${alertClass} position-fixed w-75 border-0`}
        closeClassName={`${alertText}`}
      >
        <div
          className={`d-flex align-items-center justify-content-center text-white alert-font-size`}
        >
          {alertMessage}
        </div>
      </Alert>
      {alertStyles}
      <Container fluid={true} className="p-0">
        <Row>
          <div className="col align-self-center">
            <div className="outer-container d-flex flex-column mx-auto shadow p-3 bg-white rounded">
              <Col xs={{ size: 10, offset: 1 }} md={{ size: 6, offset: 3 }}>
                <div
                  className={`text-center mt-${hideConnectingText ? '4' : '2'}`}
                >
                  <Row
                    className={isElevatUi ? '' : isSupraUi ? 'mb-5' : 'mb-3'}
                  >
                    <img
                      alt="brand"
                      className="login-logo image-fluid mw-100 mx-auto"
                      src={isTigeDemoUi ? tige_login_logo_url : logo_url}
                    />
                  </Row>
                  <p
                    className={
                      hideConnectingText
                        ? 'd-none'
                        : `text-center text-elevat-blue three-quarter-text mx-auto mb-${
                            isEnovationUi ? '2' : '4'
                          }`
                    }
                  >
                    Connecting the Industrial World
                  </p>
                  <div className="font-weight-light text-center text-text-light-gray three-quarter-text mb-4">
                    Sign in to start managing your IoT assets.
                  </div>
                </div>
                <Form onSubmit={handleSubmit}>
                  <FormGroup row>
                    <Label className="mt-1 ml-2" for="login-email">
                      Email
                    </Label>
                    <Input
                      className={`${inputClass} border-${
                        showError
                          ? 'danger'
                          : isVersatileUi
                            ? 'input-border-gray'
                            : isSupraUi
                              ? 'dark'
                              : 'elevat-blue'
                      } bg-white shadow-none`}
                      type="email"
                      name="email"
                      id="login-email"
                      value={email}
                      placeholder="name@company.com"
                      onChange={handlePropUpdate}
                      invalid={showError}
                    />
                  </FormGroup>
                  <FormGroup className="mb-0" row>
                    <Label className="ml-2" for="login-password">
                      Password
                    </Label>
                    <div className="input-group">
                      <Input
                        className={`${inputClass} border-${
                          showError
                            ? 'danger'
                            : isVersatileUi
                              ? 'input-border-gray'
                              : isSupraUi
                                ? 'dark'
                                : 'elevat-blue'
                        } shadow-none bg-white border-right-0`}
                        type={hidePassword ? 'password' : 'text'}
                        id="login-password"
                        placeholder="password"
                        name="password"
                        value={password}
                        onChange={handlePropUpdate}
                        invalid={showError}
                      />
                      <div className="input-group-append">
                        <button
                          type="button"
                          onClick={togglePasswordType}
                          className={`btn btn-link ${eyeButtonClass} border-${
                            showError
                              ? 'danger'
                              : isVersatileUi
                                ? 'input-border-gray'
                                : isSupraUi
                                  ? 'dark'
                                  : 'elevat-blue'
                          } input-group-text border-left-0 bg-transparent`}
                        >
                          <FontAwesomeIcon
                            icon={hidePassword ? faEye : faEyeSlash}
                          />
                        </button>
                      </div>
                    </div>
                  </FormGroup>
                  <div className="error-container row align-items-center">
                    {showError && (
                      <span
                        className={`text-danger error-text ${ERROR_LOGIN_FORM}`}
                      >
                        {error}
                      </span>
                    )}
                  </div>
                  <Row>
                    <button
                      className={`btn login-button${
                        isVersatileUi ? '-versatile' : ''
                      } flex-grow-1 form-border-radius d-flex align-items-center justify-content-center`}
                      type="submit"
                      disabled={loginRequestPending}
                    >
                      <span
                        className={`login-button-text${
                          isVersatileUi ? '-versatile' : ''
                        }`}
                      >
                        {loginRequestPending ? (
                          <FontAwesomeIcon
                            icon={faCircleNotch}
                            spin
                            size="lg"
                          />
                        ) : (
                          'Sign In'
                        )}
                      </span>
                    </button>
                  </Row>
                </Form>
                <div className="row text-center">
                  <button
                    className="btn btn-link mx-auto forgot-password login-form-link text-decoration-none shadow-none"
                    onClick={() =>
                      toggleResetPasswordModal(!showResetPasswordModal)
                    }
                  >
                    Forgot your password?
                  </button>
                </div>
              </Col>
              {!isVersatileUi ? (
                <div className="mt-auto">
                  <LoginFooterDisplay
                    termsModal={termsModal}
                    toggleTermsAndConditionsModal={
                      toggleTermsAndConditionsModal
                    }
                  />
                </div>
              ) : null}
            </div>
            {isVersatileUi ? (
              <div
                className="mx-auto mt-1 text-white"
                style={{ maxWidth: '620px' }}
              >
                <LoginFooterDisplay
                  termsModal={termsModal}
                  toggleTermsAndConditionsModal={toggleTermsAndConditionsModal}
                />
              </div>
            ) : null}
          </div>
        </Row>
      </Container>
      {showResetPasswordModal ? (
        <ResetPasswordModal
          isOpen={showResetPasswordModal}
          toggleModal={() => toggleResetPasswordModal(!showResetPasswordModal)}
          toggleAlert={toggleAlert}
          showResetPasswordAlert={showResetPasswordAlert}
        ></ResetPasswordModal>
      ) : null}
      {inputStyles}
      {eyeButtonStyles}
      {modalStyles}
      <style jsx>{`
        .outer-container {
          height: ${isVersatileUi ? '500' : '550'}px;
          max-width: 620px;
          color: var(--black-text);
        }
        .login-logo {
          max-height: 70px;
        }
        .login-button {
          background-color: var(--login-button-background-color);
          border: none;
          cursor: pointer;
          height: 38px;
        }
        .login-button:focus,
        .login-button:hover {
          background-color: var(--login-button-hover-background-color);
          outline: none;
        }
        .login-button-versatile {
          background-color: var(--white);
          border-color: var(--brand-primary);
          color: var(--brand-primary);
          cursor: pointer;
          height: 38px;
        }
        .login-button-versatile:focus,
        .login-button-versatile:hover {
          background-color: var(--brand-primary);
          color: var(--white);
        }
        .login-button-text {
          color: var(--white);
          font-size: 1.2rem;
        }
        .login-button-text-versatile {
          color: inherit;
          font-size: 1.2rem;
        }
        .login-button-text-versatile:focus,
        .login-button-text-versatile:hover {
          color: var(--white);
        }
        .forgot-password {
          color: var(--black-text);
          font-size: 0.75rem;
        }
        .login-form-link {
          font-size: 0.8rem !important;
          color: var(--black-text);
        }
        .login-form-link:hover {
          color: var(
            --${isVersatileUi || isSupraUi ? 'brand-primary' : 'highlight-blue'}
          ) !important;
        }
        .form-border-radius {
          border-radius: 15px;
        }
        .error-container {
          height: 40px;
        }
        .error-text {
          margin-left: 10px;
          font-size: 0.81rem;
        }
        @media (min-width: 768px) {
          .login-logo {
            max-height: 100px;
          }
          .login-form-footer {
            margin-top: 55px;
          }
          .outer-container {
            height: ${isVersatileUi ? '550' : '600'}px;
          }
        }
      `}</style>
    </CenterContainer>
  );
}

LoginDisplay.propTypes = {
  alertColor: PropTypes.string,
  alertMessage: PropTypes.string,
  alertText: PropTypes.string,
  email: PropTypes.string,
  error: PropTypes.string,
  forgotModal: PropTypes.bool,
  handleSubmit: PropTypes.func,
  handlePropUpdate: PropTypes.func,
  hidePassword: PropTypes.bool,
  loginRequestPending: PropTypes.bool,
  password: PropTypes.string,
  showAlert: PropTypes.bool,
  showError: PropTypes.bool,
  showResetPasswordAlert: PropTypes.func,
  termsModal: PropTypes.bool,
  toggleAlert: PropTypes.func,
  togglePasswordType: PropTypes.func,
  toggleTermsAndConditionsModal: PropTypes.func,
};

function LoginFooterDisplay(props) {
  const { contact_url, isSupraUi, isVersatileUi } = useContext(BrandingContext);
  return (
    <div className="login-form-footer d-flex flex-column justify-content-center align-items-center flex-md-row justify-content-md-between align-items-md-end">
      <div className="text-nowrap pl-lg-4">
        <span className="branded-text">powered by </span>
        <span className="branded-text font-weight-light">elevāt</span>
      </div>
      <div className="text-nowrap">
        <span className="branded-text">Don&apos;t have an account?</span>
        <a
          className="login-form-link ml-2 text-decoration-none shadow-none"
          href={contact_url}
          rel="noopener noreferrer"
          target="_blank"
        >
          Contact Us
        </a>
      </div>
      <div className="text-nowrap pr-lg-4">
        <button
          className="btn m-0 p-0 login-form-link text-decoration-none shadow-none"
          onClick={props.toggleTermsAndConditionsModal}
        >
          Terms and Conditions
        </button>
      </div>
      <Modal
        className={modalClass}
        isOpen={props.termsModal}
        toggle={props.toggleTermsAndConditionsModal}
        style={{ maxWidth: '620px' }}
      >
        <ModalHeader
          className="border-0"
          toggle={props.toggleTermsAndConditionsModal}
        >
          Terms of Service
        </ModalHeader>
        <ModalBody className={`${modalClass} text-left`}>
          <TermsAndConditions />
        </ModalBody>
        <ModalFooter>
          <button
            className="btn btn-outline-secondary py-0 px-3 mr-3"
            onClick={props.toggleTermsAndConditionsModal}
          >
            Close
          </button>
        </ModalFooter>
      </Modal>
      <style jsx>{`
        .login-form-footer {
          font-size: 0.8rem;
          line-height: 1.2rem;
        }
        .branded-text {
          color: var(--${isVersatileUi ? 'text-white' : 'elevat-blue'});
        }
        .login-form-link {
          font-size: 0.8rem !important;
          color: var(--${isVersatileUi ? 'elevat-blue' : 'black-text'});
        }
        .login-form-link:focus,
        .login-form-link:hover {
          color: var(
            --${isSupraUi ? 'brand-primary' : 'highlight-blue'}
          ) !important;
        }
      `}</style>
    </div>
  );
}

LoginFooterDisplay.propTypes = {
  termsModal: PropTypes.bool,
  toggleTermsAndConditionsModal: PropTypes.func,
};
